import React, { useState } from 'react';
import AwardCardGrid from './award-card-grid';
import { ContactModal } from './contact-modal';
import { ProjectModal } from './project-modal';
import { SiteLogo } from './site-logo';
import { cn } from '../../lib/utils';

// --- TYPE DEFINITIONS FOR PROPS ---
interface NavLink { label: string; href: string; }
interface ProjectFeature {
  id: string;
  title: string;
  description: string;
}

interface Project {
  id: string;
  title: string;
  description: string;
  tags: string[];
  logoSrc: string;
  logoAlt: string;
  modalImageSrc: string;
  modalImageAlt: string;
  modalImageFit?: 'contain' | 'cover';
  siteHref?: string;
  logoClassName?: string;
  logoContainerClassName?: string;
  features: ProjectFeature[];
}
interface Stat { value: string; label: string; }

export interface PortfolioPageProps {
  logo?: { initials: React.ReactNode; name: React.ReactNode; };
  navLinks?: NavLink[];
  resume?: { label: string; onClick?: () => void; };
  hero?: { titleLine1: React.ReactNode; titleLine2Gradient: React.ReactNode; subtitle: React.ReactNode; };
  ctaButtons?: { primary: { label: string; onClick?: () => void; }; secondary?: { label: string; onClick?: () => void; }; };
  projects?: Project[];
  stats?: Stat[];
  showAnimatedBackground?: boolean;
}

const PortfolioBackdrop: React.FC = React.memo(() => (
  <div className="portfolio-shell-background" aria-hidden="true">
    <div className="portfolio-shell-background__grain" />
    <div className="portfolio-shell-background__glow portfolio-shell-background__glow--left" />
    <div className="portfolio-shell-background__glow portfolio-shell-background__glow--right" />
  </div>
));

// --- DEFAULT DATA ---
const defaultData: PortfolioPageProps = {
  logo: { initials: 'MT', name: 'Meng To' },
  navLinks: [ { label: 'About', href: '#about' }, { label: 'Projects', href: '#projects' }, { label: 'Skills', href: '#skills' } ],
  resume: { label: 'Resume', onClick: undefined },
  hero: { titleLine1: 'Creative Developer &', titleLine2Gradient: 'Digital Designer', subtitle: 'I craft beautiful digital experiences through code and design. Specializing in modern web development, UI/UX design, and bringing innovative ideas to life.', },
  ctaButtons: { primary: { label: 'View My Work', onClick: undefined }, secondary: { label: 'Get In Touch', onClick: undefined }, },
  projects: [],
  stats: [ { value: '50+', label: 'Projects Completed' }, { value: '5+', label: 'Years Experience' }, { value: '15+', label: 'Happy Clients' }, ],
};

// --- MAIN CUSTOMIZABLE PORTFOLIO COMPONENT ---
const PortfolioPage: React.FC<PortfolioPageProps> = ({
  logo = defaultData.logo,
  navLinks = defaultData.navLinks,
  resume = defaultData.resume,
  hero = defaultData.hero,
  ctaButtons = defaultData.ctaButtons,
  projects = defaultData.projects,
  stats = defaultData.stats,
  showAnimatedBackground = true,
}) => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  return (
    <div className="bg-background text-foreground geist-font flex h-full min-h-0 w-full flex-col overflow-hidden overscroll-none">
      {showAnimatedBackground && <PortfolioBackdrop />}
      <ContactModal 
        isOpen={isContactModalOpen} 
        onClose={() => setIsContactModalOpen(false)} 
      />
      <ProjectModal
        isOpen={!!selectedProject}
        onClose={() => setSelectedProject(null)}
        project={selectedProject}
      />
      <header className="relative z-30 w-full shrink-0">
        <nav className="w-full px-4 sm:px-6 pt-3 sm:pt-4">
            <div className="max-w-6xl mx-auto flex justify-between items-center">
                <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
                    {/* Logo – no background box, larger size */}
                    <div className="flex-shrink-0" style={{ minWidth: '4rem', minHeight: '4rem' }}>
                      <SiteLogo
                        alt="Site Logo"
                        className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 lg:w-28 lg:h-28"
                      />
                    </div>
                </div>
                <div className="hidden md:flex items-center space-x-8">
                    {navLinks!.map(link => (
                        <a key={link.label} href={link.href} className="text-muted-foreground hover:text-foreground transition-colors inter-font text-base">{link.label}</a>
                    ))}
                </div>
                <button onClick={() => setIsContactModalOpen(true)} className="glass-button px-3 py-1.5 sm:px-5 sm:py-2.5 rounded-lg text-foreground text-sm sm:text-base font-medium inter-font whitespace-nowrap">Contact</button>
            </div>
        </nav>
      </header>
      <div className="relative min-h-0 w-full flex-1 overflow-y-auto overflow-x-hidden overscroll-contain">
        <div
          aria-hidden="true"
          className="mt-4 mb-12 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent sm:mb-16"
        />
        <main id="about" className="relative w-full min-h-screen flex flex-col items-center justify-center px-4 sm:px-6 pb-12 sm:pb-20 overflow-x-hidden">
            <div className="hero-spotlight" aria-hidden="true" />
            <div className="max-w-6xl mx-auto text-center w-full relative z-10">
                <div className="mb-6 sm:mb-8">
                    <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl leading-[1.1] geist-font font-light text-foreground tracking-tight mb-3 sm:mb-4">
                        {hero!.titleLine1}
                        <span className="gradient-text block pb-1 tracking-tight">{hero!.titleLine2Gradient}</span>
                    </h1>
                    <p className="text-base sm:text-lg md:text-xl lg:text-2xl max-w-3xl leading-relaxed inter-font font-light text-muted-foreground mx-auto px-2">{hero!.subtitle}</p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center">
                    <button onClick={ctaButtons!.primary.onClick} className="primary-button px-6 sm:px-8 py-3 sm:py-4 text-foreground rounded-lg font-medium text-sm sm:text-base w-full sm:w-auto sm:min-w-[180px]">{ctaButtons!.primary.label}</button>
                    {ctaButtons!.secondary && (
                        <button onClick={() => setIsContactModalOpen(true)} className="glass-button w-full sm:w-auto sm:min-w-[180px] inter-font text-sm sm:text-base font-medium text-foreground rounded-lg px-6 sm:px-8 py-3 sm:py-4">{ctaButtons!.secondary.label}</button>
                    )}
                </div>
                <div className="divider my-12 sm:my-16" />
                <div id="projects" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 max-w-[72rem] mx-auto w-full px-2 sm:px-0">
                    {projects!.map((project) => {
                      return (
                        <button
                            key={project.id}
                            type="button"
                            className="glass-card flex w-full cursor-pointer flex-col rounded-xl p-4 text-left hover:bg-background/10 active:bg-background/20 md:h-full sm:rounded-2xl sm:p-6"
                            onClick={() => setSelectedProject(project)}
                        >
                            <div className="project-image mb-4 flex h-28 items-center justify-center rounded-lg border border-white/10 bg-background/40 p-6 sm:h-32 sm:rounded-xl">
                              <div
                                className={cn(
                                  'flex h-full w-full items-center justify-center',
                                  project.logoContainerClassName,
                                )}
                              >
                                <img
                                  src={project.logoSrc}
                                  alt={project.logoAlt}
                                  className={cn('h-full w-full object-contain', project.logoClassName)}
                                />
                              </div>
                            </div>
                            <h3 className="mb-2 text-lg font-medium text-card-foreground geist-font sm:text-xl">{project.title}</h3>
                            <p className="text-sm text-muted-foreground inter-font sm:text-base md:min-h-20">{project.description}</p>
                            <div className="mt-3 flex flex-wrap content-start gap-1.5 sm:mt-4 sm:gap-2 md:min-h-16">
                                {project.tags.map(tag => (
                                    <span
                                      key={tag}
                                      className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-xs text-muted-foreground sm:px-3 sm:text-sm"
                                    >
                                      {tag}
                                    </span>
                                ))}
                            </div>
                        </button>
                      );
                    })}
                </div>
                <div className="divider my-12 sm:my-16" />
                <div id="skills">
                  <AwardCardGrid />
                </div>
            </div>
        </main>
      </div>
    </div>
  );
};

export {PortfolioPage};
